<template>
  <FocusTrap
    active
    :initial-focus="getInitialFocus"
  >
    <div
      :style="{ ...cssVars, '--form-link-color': linkColor }"
      class="form-modal-v2"
      role="dialog"
      :aria-labelledby="formTitle"
      aria-modal="true"
      @wheel.self.prevent
      @touchmove.self.prevent
    >
      <div
        ref="form-title"
        class="sr-only"
        aria-live="polite"
        role="alert"
      />
      <div
        data-testid="forms-modal-container"
        class="form-modal-v2_container"
        @wheel="setAllowParentScroll(false)"
      >
        <div class="form-modal-v2_controls">
          <button
            ref="close-button"
            class="close-icon"
            @click="handleModalClose"
          >
            <modal-close-icon
              data-testid="forms-modal-close"
              class="form-modal-v2_close"
            />
          </button>
        </div>
        <div class="form-modal-v2_form">
          <div
            v-if="shouldDisplayStatusView"
            class="form-modal-v2_status-display"
          >
            <h3
              :id="formTitle"
              class="form-modal-v2_title"
            >
              {{ formTitle }}
            </h3>
            <div class="form-modal-v2_status-content">
              <component
                :is="statusIcon"
                v-if="hasSubmissionError || submitSuccess"
                class="form-modal-v2_status-icon"
              />
              <p
                aria-live="polite"
                role="alert"
                class="form-modal-v2_status-text"
              >
                {{ statusText }}
              </p>
              <form-button
                v-if="hasSubmissionError"
                class="form-modal-v2_status-action"
                @click="resetForm"
              >
                {{ $t('forms.tryAgain') }}
              </form-button>
            </div>
            <a
              :href="privacyPolicyUrl"
              :target="privacyPolicyTarget"
              class="form-modal-v2_privacy-policy"
            >
              {{ $t('forms.privacyPolicy') }}
            </a>
          </div>

          <fc-form-renderer
            v-else-if="answers.length && !fetchingBrand"
            v-model:answers="answers"
            v-model:answers-form-data-format="answersFormData"
            :branding="branding"
            :close-date="closeDate"
            :description="getDescription"
            :form="form"
            :logo="logo"
            :questions="questions"
            :primary-color="primaryColor"
            :title="formTitle"
            :privacy-policy-url="privacyPolicyUrl"
            :privacy-policy-target="privacyPolicyTarget"
            :hide-header="!logo || !branding"
            :invalid-otp-code="invalidOtpCode"
            @submit="handleFormSubmit"
            @send-otp-code="sendCode"
            @resend-otp-code="sendCode"
            @confirm-otp-code="confirmCode"
            @close-modal="handleModalClose"
          />
        </div>
      </div>
    </div>
  </FocusTrap>
</template>

<script>
import { isBefore, parseISO } from 'date-fns';
import { FocusTrap } from 'focus-trap-vue';
import get from 'lodash/get';
import cssVarsMixin from './components/mixins/color-vars';
import ModalCloseIcon from '../../global/svg/ModalCloseIcon.vue';
import SuccessCheck from './components/svg/SuccessCheck.vue';
import ErrorBang from './components/svg/ErrorBang.vue';
import FormButton from './components/dscl/FormButton.vue';

const CLOSE_DATE_FORMAT_OPTIONS = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  hour12: true,
  timeZoneName: 'short',
};

export default {
  name: 'FormModalV2',
  components: {
    ErrorBang,
    FormButton,
    ModalCloseIcon,
    SuccessCheck,
    FocusTrap,
  },
  mixins: [cssVarsMixin],
  props: {
    form: {
      type: Object,
      default: () => ({}),
    },
    questions: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    'close',
    'submitStart',
    'submitSuccess',
    'submitError',
    'fetchBrandStart',
    'fetchBrandSuccess',
    'fetchBrandError',
  ],
  data() {
    return {
      answers: [],
      brand: {},
      fetchingBrand: false,
      fetchBrandSuccess: false,
      hasFetchBrandError: false,
      hasSubmissionError: false,
      privacyPolicyUrl: 'https://www.apptegy.com/privacy-policy/',
      privacyPolicyTarget: '_blank',
      submitting: false,
      submitSuccess: false,
      respondentEmail: null,
      invalidOtpCode: false,
      viewedAt: new Date().toISOString(),
      answersFormData: new FormData(),
    };
  },
  computed: {
    branding() {
      return this.brand?.name || '';
    },
    closeDate() {
      const closeDate = this.form?.attributes?.close_date;
      if (!closeDate) return '';
      return this.$d(closeDate, CLOSE_DATE_FORMAT_OPTIONS);
    },
    getDescription() {
      return get(this.form, 'attributes.description', '');
    },
    shouldDisplayStatusView() {
      return this.isNotAvailable || this.hasSubmissionError || this.submitSuccess;
    },
    formTitle() {
      return this.form?.title || this.form?.attributes?.name || '';
    },
    parsedISODate() {
      return parseISO(this.form?.attributes?.close_date);
    },
    closeDateIsBeforeNow() {
      return isBefore(this.parsedISODate, new Date());
    },
    isNotAvailable() {
      const expirationDateISO = this.form?.attributes?.close_date;
      return !!expirationDateISO && this.closeDateIsBeforeNow;
    },
    linkColor() {
      return this.brand?.link_color || this.headingColor;
    },
    logo() {
      return this.brand?.logo || '';
    },
    primaryColor() {
      return this.brand?.primary_color || this.headingColor;
    },
    questionsMap() {
      if (!Array.isArray(this.questions)) return {};
      return this.questions.reduce(
        (acc, question) => ({
          ...acc,
          [question.id]: question,
        }),
        {},
      );
    },
    statusIcon() {
      if (this.submitSuccess) return 'SuccessCheck';
      if (this.hasSubmissionError) return 'ErrorBang';
      return 'span';
    },
    statusText() {
      if (this.isNotAvailable) return this.$t('forms.notAvailable');
      if (this.submitSuccess) return this.$t('forms.submitSuccess');
      if (this.hasSubmissionError) return this.$t('forms.submitError');
      return '';
    },
    submitUrl() {
      const uniqueId = this.form?.attributes?.unique_id;
      if (!uniqueId) return '';
      return `${useRuntimeConfig().public.FORMS_SERVICE_API_URL}api/v1/forms/${uniqueId}/responses`;
    },
  },
  created() {
    this.fetchBrand();
    this.setAnswersToDefault();
  },
  mounted() {
    this.setAllowParentScroll(false);
    this.setAnswersToDefault();
    this.$refs['form-title'].innerText = this.formTitle;
    window.addEventListener('keydown', this.onKeydown);
  },
  beforeUnmount() {
    this.setAllowParentScroll(true);
    window.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    getInitialFocus() {
      if (this.isNotAvailable) {
        return this.$el.querySelector('.close-icon');
      }
      switch (this.questions[0].elementType) {
        case 'LONG_ANSWER': {
          return this.$el.querySelector('textarea');
        }
        case 'E_SIGNATURE': {
          return this.$el.querySelector('.add-signature');
        }
        case 'SCALE': {
          return this.$el.querySelector('.scale-choice');
        }
        case 'FILE_UPLOAD': {
          return this.$el.querySelector('.file-upload');
        }
        default: {
          return this.$el.querySelector('input');
        }
      }
    },
    setAllowParentScroll(allowScroll) {
      if (!allowScroll && document.body.style.overflow === 'hidden') return;
      document.body.style.overflow = allowScroll ? 'visible' : 'hidden';
    },
    async handleFormSubmit() {
      try {
        if (this.submitting) return;
        this.setSubmitStatus('start');
        if (!this.submitUrl) throw new Error('Not a valid submit url');
        const answers = this.answersFormData;
        answers.append('data[attributes][viewed_at]', this.viewedAt);

        const submitRequest = await this.$axios.post(this.submitUrl, answers, {
          headers: {
            'X-otp-email': this.respondentEmail,
            'Content-Type': 'multipart/form-data',
          },
        });
        if (submitRequest.status !== 200) {
          throw new Error('Form submission not successful');
        }
        this.setSubmitStatus('success');
      } catch (error) {
        this.setSubmitStatus('error', error);
      }
      this.$refs['close-button'].focus();
    },
    onKeydown(e) {
      if (e.key === 'Escape') {
        this.$emit('close');
      }
    },
    handleModalClose() {
      if (this.submitting) return;
      this.$emit('close');
    },
    setSubmitStatus(status, emitPayload) {
      switch (status) {
        case 'start':
          this.submitting = true;
          this.submitSuccess = false;
          this.hasSubmissionError = false;
          this.$emit('submitStart', emitPayload);
          break;
        case 'success':
          this.submitSuccess = true;
          this.hasSubmissionError = false;
          this.submitting = false;
          this.$emit('submitSuccess', emitPayload);
          break;
        case 'error':
          this.submitSuccess = false;
          this.hasSubmissionError = true;
          this.submitting = false;
          this.$emit('submitError', emitPayload);
          break;
        case 'initial':
          this.submitSuccess = false;
          this.hasSubmissionError = false;
          this.submitting = false;
          break;
        default:
          break;
      }
    },
    setFetchingBrandStatus(status, emitPayload) {
      switch (status) {
        case 'start':
          this.fetchingBrand = true;
          this.fetchBrandSuccess = false;
          this.hasFetchBrandError = false;
          this.$emit('fetchBrandStart', emitPayload);
          break;
        case 'success':
          this.fetchBrandSuccess = true;
          this.hasFetchBrandError = false;
          this.fetchingBrand = false;
          this.$emit('fetchBrandSuccess', emitPayload);
          break;
        case 'error':
          this.hasFetchBrandError = true;
          this.fetchBrandSuccess = false;
          this.fetchingBrand = false;
          this.$emit('fetchBrandError', emitPayload);
          break;
        case 'initial':
          this.submitSuccess = false;
          this.hasFetchBrandError = false;
          this.fetchingBrand = false;
          break;
        default:
          break;
      }
    },
    setAnswersToDefault() {
      this.answers = this.questions.map((question) => ({
        question_id: question.id,
        answer: '',
        accepted_at: this.getQuestionType(question.id) === 'E_SIGNATURE' ? '' : undefined,
      }));
    },
    resetForm() {
      this.setAnswersToDefault();
      this.setSubmitStatus('initial');
    },
    async fetchBrand() {
      try {
        const brandId = this.form?.attributes?.brand_id;
        if (!brandId) return;
        this.setFetchingBrandStatus('start');
        const { data: brand } = await this.$axios.get(
          `${useRuntimeConfig().public.ENGAGE_API_URL}/api/v1/public/brands/${brandId}`,
        );
        this.brand = brand;
        this.setFetchingBrandStatus('success', brand);
      } catch (error) {
        this.setFetchingBrandStatus('error', error);
      }
    },
    async sendCode(email) {
      this.respondentEmail = this.respondentEmail || email;
      await this.$axios.post(`${useRuntimeConfig().public.FORMS_SERVICE_API_URL}api/v1/email_verification`, {
        data: {
          attributes: {
            email: this.respondentEmail,
            form_unique_id: this.form?.attributes?.unique_id,
          },
        },
      });
    },
    async confirmCode(code) {
      this.invalidOtpCode = false;
      const {
        data: { success },
      } = await this.$axios.post(`${useRuntimeConfig().public.FORMS_SERVICE_API_URL}api/v1/otp_verification`, {
        data: {
          attributes: {
            email: this.respondentEmail,
            otp: code,
          },
        },
      });
      if (!success) {
        this.invalidOtpCode = true;
      } else {
        this.handleFormSubmit();
      }
    },
    getQuestionType(questionId) {
      return this.questionsMap?.[questionId]?.elementType;
    },
  },
};
</script>
<style lang="scss" scoped>
.form-modal-v2 {
  --modalIndex: 99;
  z-index: var(--modalIndex, 99);
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  &::before {
    content: '';
    z-index: -1;
    position: absolute;
    left: 0;
    right: 0;
    display: block;
    width: 100%;
    height: 100%;
    opacity: 0.9;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.9)), var(--overlay-color);
  }

  :deep(a) {
    color: var(--form-link-color) !important; // important added to override tailwind classes inside fc-form-renderer
  }
}
.form-modal-v2_container {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 960px;
  height: 100%;
  max-height: 800px;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0px 6px 20px rgba(0, 0, 0, 0.06);

  @media screen and (max-width: 599px) {
    max-height: 100%;
  }
}
.form-modal-v2_controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 40px;
}
.close-icon {
  cursor: pointer;
  padding: 0;
  background: none;
  border: none;
  margin-left: auto;
}
.form-modal-v2_close {
  margin-left: auto;
}
.form-modal-v2_form {
  flex: 1 1 auto;
  overflow-y: auto;
  padding-left: 0;
  padding-right: 0;
}

.form-modal-v2_status-display {
  height: 100%;
  width: 100%;
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.form-modal-v2_status-icon {
  margin-bottom: 24px;
}
.form-modal-v2_status-text {
  display: inline-block;
  font-size: 24px;
  line-height: 32px;
  font-weight: 900;
  letter-spacing: 0.8px;
}
.form-modal-v2_title {
  font-size: 32px;
  margin-bottom: 24px;
  margin-bottom: auto;
  font-weight: bold;
}
.form-modal-v2_privacy-policy {
  display: inline-block;
  margin-bottom: 64px;
  &,
  &:visited,
  &:active {
    color: var(--form-link-color);
  }
}
.form-modal-v2_status-content {
  margin-bottom: auto;
}
.form-modal-v2_status-action {
  display: block;
  width: 100%;
  max-width: 350px;
  padding: 16px 6px;
  margin: 7vh auto 2vh;
  font-size: 20px;
  line-height: 1rem;
  color: white;
  border-radius: 3px;

  &:before {
    display: none;
  }

  &,
  &:focus {
    border: 0;
    background: var(--primary-forms-color);
  }
}
</style>
